import React, { useState, useEffect } from 'react';
import { useRequestQueue } from '../services/requestQueue';

/**
 * RequestNotificationPanel Component
 * 
 * Shows a floating panel in the bottom-right corner that displays:
 * - Number of pending requests
 * - Failed requests with error details
 * - Recent successful requests
 * 
 * Users can expand/collapse the panel to see details
 */
export const RequestNotificationPanel = () => {
  const { queue, errors, clearErrors, pendingCount, failedCount, successCount } = useRequestQueue();
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoHideSuccess, setAutoHideSuccess] = useState(true);

  // Auto-hide success messages after 5 seconds
  useEffect(() => {
    if (successCount > 0 && autoHideSuccess) {
      const timer = setTimeout(() => {
        // Success messages are automatically removed by the components that add them
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successCount, autoHideSuccess]);

  // Show collapsed button if there are any pending or failed requests
  const hasActiveRequests = pendingCount > 0 || failedCount > 0;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Collapsed View - Shows summary button */}
      {!isExpanded && hasActiveRequests && (
        <button
          onClick={() => setIsExpanded(true)}
          className={`px-4 py-2 rounded-lg shadow-lg text-white font-semibold transition-all ${
            failedCount > 0 
              ? 'bg-red-500 hover:bg-red-600' 
              : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {failedCount > 0 && '⚠️ '}
          Requests ({pendingCount} pending{failedCount > 0 ? `, ${failedCount} failed` : ''})
        </button>
      )}

      {/* Expanded View - Shows detailed panel */}
      {isExpanded && (
        <div className="bg-white border border-gray-300 rounded-lg shadow-xl w-96 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center bg-gray-50">
            <h3 className="font-bold text-lg">Request Queue</h3>
            <button 
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700 text-xl"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          
          {/* Content - Scrollable */}
          <div className="overflow-y-auto flex-1">
            {/* Pending Requests Section */}
            {pendingCount > 0 && (
              <div className="p-3 border-b bg-yellow-50">
                <h4 className="font-semibold text-yellow-800 mb-2">
                  ⏳ Pending ({pendingCount})
                </h4>
                {queue
                  .filter(r => r.status === 'pending')
                  .slice(0, 10) // Show max 10 pending
                  .map(req => (
                    <div key={req.id} className="text-sm p-2 mb-1 bg-white rounded border border-yellow-200">
                      <div className="font-medium text-gray-700">
                        {req.type || 'Request'}
                      </div>
                      {req.description && (
                        <div className="text-gray-600 text-xs mt-1">{req.description}</div>
                      )}
                      {req.url && (
                        <div className="text-gray-500 text-xs mt-1 truncate" title={req.url}>
                          {req.url}
                        </div>
                      )}
                    </div>
                  ))}
                {pendingCount > 10 && (
                  <div className="text-xs text-gray-500 mt-1">
                    +{pendingCount - 10} more...
                  </div>
                )}
              </div>
            )}

            {/* Failed Requests / Errors Section */}
            {errors.length > 0 && (
              <div className="p-3 border-b bg-red-50">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-red-800">
                    ❌ Errors ({errors.length})
                  </h4>
                  <button 
                    onClick={clearErrors}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear All
                  </button>
                </div>
                {errors.slice(0, 20).map(err => (
                  <div key={err.id} className="text-sm p-2 mb-2 bg-white rounded border border-red-200">
                    <div className="font-semibold text-red-700">
                      {err.type || 'Error'}
                    </div>
                    <div className="text-gray-700 mt-1">
                      {err.message || 'An error occurred'}
                    </div>
                    {err.url && (
                      <div className="text-xs text-gray-500 mt-1 truncate" title={err.url}>
                        URL: {err.url}
                      </div>
                    )}
                    {err.status && (
                      <div className="text-xs text-gray-500 mt-1">
                        Status: {err.status}
                      </div>
                    )}
                    {err.response && (
                      <div className="text-xs text-gray-500 mt-1">
                        Response: {JSON.stringify(err.response).substring(0, 100)}...
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(err.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                {errors.length > 20 && (
                  <div className="text-xs text-gray-500 mt-1">
                    +{errors.length - 20} more errors...
                  </div>
                )}
              </div>
            )}

            {/* Success Messages Section */}
            {successCount > 0 && (
              <div className="p-3 bg-green-50">
                <h4 className="font-semibold text-green-800 mb-2">
                  ✓ Recent Success ({successCount})
                </h4>
                {queue
                  .filter(r => r.status === 'success')
                  .slice(-5) // Show last 5 successful requests
                  .map(req => (
                    <div key={req.id} className="text-sm p-2 mb-1 bg-white rounded border border-green-200">
                      <div className="text-gray-700">
                        {req.description || req.type || 'Request completed'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(req.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Empty State */}
            {pendingCount === 0 && errors.length === 0 && successCount === 0 && (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">✓</div>
                <div>No active requests</div>
              </div>
            )}
          </div>

          {/* Footer with summary */}
          <div className="p-3 border-t bg-gray-50 text-xs text-gray-600">
            Total: {queue.length} requests | 
            Pending: {pendingCount} | 
            Failed: {failedCount} | 
            Success: {successCount}
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestNotificationPanel;

